"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { useTranslations } from "next-intl";
import { usePortfolioEntrance } from "../../hero/parts/PortfolioSequence";
import DeckCard from "./DeckCard";
import DeckLayer from "./DeckLayer";
import useDeckPosition from "./useDeckPosition";

export default function ArrowDeck({ clients, className = "" }) {
  const t = useTranslations("home.portfolio.deck");
  const { p, index, goToIndex, step, positionRef, goTo } = useDeckPosition(clients.length);
  const stageRef = useRef(null);
  const gesture = useRef(null);
  const { entered, finishEntrance } = usePortfolioEntrance(stageRef);
  const onScreen = useInView(stageRef, { amount: 0.5 });
  const [playing, setPlaying] = useState(true);
  const stopAutoplay = useCallback(() => setPlaying(false), []);

  useEffect(() => {
    if (!playing || !onScreen || !entered) return;
    const timer = window.setInterval(() => step(1), 6000);
    return () => window.clearInterval(timer);
  }, [onScreen, entered, playing, step]);

  return (
    <div className={className}>
      <div ref={stageRef} onMouseEnter={stopAutoplay} onFocusCapture={stopAutoplay}
        className="relative isolate overflow-hidden py-8">
        <div
          onPointerDown={event => {
            if (event.button !== 0 || event.target.closest("a, button")) return;
            stopAutoplay(); p.stop();
            gesture.current = { x: event.clientX, base: positionRef.current };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={event => {
            if (!gesture.current) return;
            p.set(gesture.current.base - (event.clientX - gesture.current.x) / (stageRef.current?.offsetWidth || 1) * 2);
          }}
          onPointerUp={event => {
            if (!gesture.current) return;
            const travel = (gesture.current.x - event.clientX) / (stageRef.current?.offsetWidth || 1);
            goTo(gesture.current.base + (Math.abs(travel) > 0.08 ? Math.sign(travel) : 0));
            gesture.current = null;
            event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={() => { gesture.current = null; goTo(positionRef.current); }}
          className="relative mx-auto cursor-grab active:cursor-grabbing touch-pan-y select-none h-[clamp(540px,68svh,660px)] w-[82%] max-w-[1120px]">
          {clients.map((client, position) => (
            <DeckLayer key={client.id} index={position} count={clients.length} p={p} horizontal entered={entered} onEntered={finishEntrance}>
              <DeckCard client={client} className="h-full w-full rounded-3xl"
                sizes="(min-width: 1440px) 1120px, 82vw" />
            </DeckLayer>
          ))}
        </div>
      </div>
      <div className="flex justify-center gap-1 py-4" aria-label="Portfolio navigation">
        {clients.map((client, target) => (
          <button key={client.id} type="button"
            onClick={() => { stopAutoplay(); goToIndex(target); }}
            aria-label={t("goTo", { name: client.name })} aria-current={index === target}
            className="flex h-11 w-8 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-blue">
            <span aria-hidden className={`h-1.5 rounded-full transition-[width,background-color] ${index === target ? "w-7 bg-sky-blue" : "w-1.5 bg-white/40"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}
